import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faDownload, faFilePdf, faImages, faSpinner, faTrashCan, faUpload } from '@fortawesome/free-solid-svg-icons';
import {
    addInboundBookingComment,
    cancelInboundBooking,
    changeInboundBookingStatus,
    createInboundBooking,
    deleteInboundBookingPackingList,
    deleteInboundBookingPhoto,
    downloadInboundBookingLabels,
    downloadInboundBookingPackingList,
    getInboundBooking,
    listBookingProducts,
    listInboundBookings,
    updateInboundBooking,
    uploadInboundBookingPackingList,
    uploadInboundBookingPhoto,
} from '../../api/inboundBookings';
import { useAlert } from '../../providers/AlertProvider';
import '../css/InboundBookings.css';

const emptyForm = {
    supplier: '',
    vehicle_type: 'container',
    container_size: '20ft',
    vehicle_reference: '',
    delivery_reference: '',
    requested_date: '',
    requested_time: '',
    unloading_method: 'palletised',
    customer_notes: '',
    items: [{ product_id: '', expected_quantity: '', cases_per_pallet: '', goods_description: '' }],
};

const STATUS_LABELS = {
    requested: 'Requested', accepted: 'Accepted', rejected: 'Rejected',
    arrived_onsite: 'Arrived onsite', unloading: 'Unloading',
    completed: 'Completed', cancelled: 'Cancelled',
};

const NEXT_ACTIONS = {
    requested: [['accepted', 'Accept'], ['rejected', 'Reject']],
    accepted: [['arrived_onsite', 'Mark arrived onsite']],
    arrived_onsite: [['unloading', 'Start unloading'], ['completed', 'Complete']],
    unloading: [['completed', 'Complete']],
};

const formatDateTime = (value) => value
    ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Not recorded';

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const productLabel = (product) => [product?.product_code, product?.product_name]
    .filter(Boolean)
    .join(' · ');

function ProductCombobox({ products, value, onChange }) {
    const containerRef = useRef(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const selectedProduct = products.find((product) => product.id === value);

    useEffect(() => {
        if (selectedProduct) setQuery(productLabel(selectedProduct));
    }, [selectedProduct]);

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (!containerRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, []);

    const filteredProducts = useMemo(() => {
        const term = query.trim().toLowerCase();
        const matches = term
            ? products.filter((product) => [
                product.product_code,
                product.product_name,
                product.barcode,
                product.batch_no,
            ].some((field) => String(field || '').toLowerCase().includes(term)))
            : products;
        return matches.slice(0, 50);
    }, [products, query]);

    const chooseProduct = (product) => {
        onChange(product.id);
        setQuery(productLabel(product));
        setOpen(false);
    };

    const handleKeyDown = (event) => {
        if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
            setOpen(true);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, filteredProducts.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter' && open && filteredProducts[activeIndex]) {
            event.preventDefault();
            chooseProduct(filteredProducts[activeIndex]);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className="inbound-product-combobox" ref={containerRef}>
            <input
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls="inbound-product-options"
                placeholder="Type a product code or name"
                value={query}
                onFocus={() => setOpen(true)}
                onChange={(event) => {
                    setQuery(event.target.value);
                    onChange('');
                    setActiveIndex(0);
                    setOpen(true);
                }}
                onKeyDown={handleKeyDown}
            />
            {open && (
                <div className="inbound-product-options" id="inbound-product-options" role="listbox">
                    {filteredProducts.length === 0 ? (
                        <div className="inbound-product-empty">No matching products</div>
                    ) : filteredProducts.map((product, index) => (
                        <button
                            type="button"
                            role="option"
                            aria-selected={product.id === value}
                            className={index === activeIndex ? 'active' : ''}
                            key={product.id}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => chooseProduct(product)}
                        >
                            <strong>{product.product_code}</strong>
                            <span>{product.product_name}</span>
                            {product.batch_no && <small>Batch {product.batch_no}</small>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function InboundBookingsManagement({
    isCustomer = false,
    isStaff = false,
    isAdmin = false,
    profile = null,
    selectedCustomerId = null,
    selectedCustomerName = null,
}) {
    const { showAlert } = useAlert();
    const customerId = isCustomer ? profile?.customer_id : selectedCustomerId;
    const bookingListCustomerId = isCustomer ? profile?.customer_id : null;
    const [bookings, setBookings] = useState([]);
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [downloadingLabels, setDownloadingLabels] = useState(false);
    const [photoDragActive, setPhotoDragActive] = useState(false);
    const [photoUploadProgress, setPhotoUploadProgress] = useState(null);
    const [deletingPhotoId, setDeletingPhotoId] = useState(null);
    const [packingListBusy, setPackingListBusy] = useState(false);
    const [packingListDragActive, setPackingListDragActive] = useState(false);
    const photoInputRef = useRef(null);
    const photoDragDepthRef = useRef(0);
    const packingListInputRef = useRef(null);
    const packingListDragDepthRef = useRef(0);

    const alert = useCallback((message, type = 'danger') => showAlert({
        message, type, buttons: [{ label: 'Close', type: 'primary' }],
    }), [showAlert]);

    const loadBookings = useCallback(async () => {
        try {
            const data = await listInboundBookings(bookingListCustomerId);
            setBookings(data.bookings || []);
        } catch (error) {
            alert(error.message);
        }
    }, [bookingListCustomerId, alert]);

    const loadProducts = useCallback(async (scopeId) => {
        if (!scopeId) {
            setProducts([]);
            return;
        }
        try {
            setProducts(await listBookingProducts(scopeId));
        } catch (error) {
            alert(error.message);
        }
    }, [alert]);

    useEffect(() => { loadBookings(); }, [loadBookings]);
    useEffect(() => { loadProducts(customerId); }, [customerId, loadProducts]);

    const openDetail = async (bookingId) => {
        setBusy(true);
        try {
            const data = await getInboundBooking(bookingId);
            setSelected(data.booking);
            if (!isCustomer) await loadProducts(data.booking.customer_id);
        } catch (error) {
            alert(error.message);
        } finally {
            setBusy(false);
        }
    };

    const startCreate = () => {
        if (!customerId) {
            alert('Select a customer in Warehouse before creating a booking.', 'warning');
            return;
        }
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(true);
    };

    const startEdit = () => {
        setForm({
            supplier: selected.supplier || '',
            vehicle_type: selected.vehicle_type,
            container_size: selected.container_size || '',
            vehicle_reference: selected.vehicle_reference || '',
            delivery_reference: selected.delivery_reference || '',
            requested_date: selected.requested_date || '',
            requested_time: (selected.requested_time || '').slice(0, 5),
            unloading_method: selected.unloading_method,
            customer_notes: selected.customer_notes || '',
            items: selected.items.map((item) => ({
                product_id: item.product_id,
                expected_quantity: item.expected_quantity,
                cases_per_pallet: item.cases_per_pallet || '',
                goods_description: item.goods_description || '',
            })),
        });
        setEditingId(selected.id);
        setShowForm(true);
    };

    const setField = (field, value) => setForm((current) => ({
        ...current,
        [field]: value,
        ...(field === 'vehicle_type' && value === 'trailer' ? { container_size: '' } : {}),
        ...(field === 'vehicle_type' && value === 'container' && !current.container_size ? { container_size: '20ft' } : {}),
    }));

    const setItem = (index, field, value) => setForm((current) => ({
        ...current,
        items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
    }));

    const saveBooking = async (event) => {
        event.preventDefault();
        if (form.items.some((item) => !item.product_id)) {
            alert('Select a product from the filtered results for every line.', 'warning');
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, customer_id: customerId };
            if (editingId) await updateInboundBooking(editingId, payload);
            else await createInboundBooking(payload);
            setShowForm(false);
            setEditingId(null);
            await loadBookings();
            if (selected?.id === editingId) await openDetail(editingId);
            alert(editingId ? 'Booking amended and submitted for approval.' : 'Inbound booking requested.', 'success');
        } catch (error) {
            alert(error.message);
        } finally {
            setBusy(false);
        }
    };

    const transition = async (status) => {
        let note = null;
        if (status === 'rejected') {
            note = window.prompt('Reason for rejection:');
            if (!note) return;
        }
        setBusy(true);
        try {
            await changeInboundBookingStatus(selected.id, status, note);
            await loadBookings();
            await openDetail(selected.id);
        } catch (error) {
            alert(error.message);
        } finally {
            setBusy(false);
        }
    };

    const confirmCancellation = async (bookingId) => {
        setBusy(true);
        try {
            await cancelInboundBooking(bookingId);
            await loadBookings();
            await openDetail(bookingId);
            alert('Inbound booking cancelled.', 'success');
        } catch (error) {
            alert(error.message);
        } finally {
            setBusy(false);
        }
    };

    const cancel = () => {
        const bookingId = selected.id;
        showAlert({
            title: 'Cancel inbound booking',
            message: 'Are you sure you want to cancel this inbound booking?',
            type: 'danger',
            buttons: [
                { label: 'Keep booking', type: 'secondary' },
                {
                    label: 'Cancel booking',
                    type: 'danger',
                    onClick: () => confirmCancellation(bookingId),
                },
            ],
        });
    };

    const submitComment = async (event) => {
        event.preventDefault();
        if (!comment.trim()) return;
        try {
            await addInboundBookingComment(selected.id, comment);
            setComment('');
            await openDetail(selected.id);
        } catch (error) {
            alert(error.message);
        }
    };

    const uploadPhotos = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const invalidFile = files.find((file) => (
            !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024
        ));
        if (invalidFile) {
            alert('Photos must be JPEG, PNG or WebP images no larger than 10MB.', 'warning');
            return;
        }
        setPhotoUploadProgress({ current: 0, total: files.length });
        try {
            for (const [index, file] of files.entries()) {
                await uploadInboundBookingPhoto(selected.id, file);
                setPhotoUploadProgress({ current: index + 1, total: files.length });
            }
            await openDetail(selected.id);
            alert(`${files.length} arrival photo${files.length === 1 ? '' : 's'} uploaded.`, 'success');
        } catch (error) {
            alert(error.message);
        } finally {
            setPhotoUploadProgress(null);
        }
    };

    const handlePhotoInput = async (event) => {
        await uploadPhotos(event.target.files);
        event.target.value = '';
    };

    const handlePhotoDrop = (event) => {
        event.preventDefault();
        photoDragDepthRef.current = 0;
        setPhotoDragActive(false);
        if (!photoUploadProgress) uploadPhotos(event.dataTransfer.files);
    };

    const handlePhotoDragEnter = (event) => {
        event.preventDefault();
        photoDragDepthRef.current += 1;
        setPhotoDragActive(true);
    };

    const handlePhotoDragLeave = (event) => {
        event.preventDefault();
        photoDragDepthRef.current = Math.max(0, photoDragDepthRef.current - 1);
        if (photoDragDepthRef.current === 0) setPhotoDragActive(false);
    };

    const confirmPhotoDeletion = (photo) => {
        showAlert({
            title: 'Delete arrival photo',
            message: `Delete ${photo.filename}? This cannot be undone.`,
            type: 'danger',
            buttons: [
                { label: 'Keep photo', type: 'secondary' },
                {
                    label: 'Delete photo',
                    type: 'danger',
                    onClick: async () => {
                        setDeletingPhotoId(photo.id);
                        try {
                            await deleteInboundBookingPhoto(selected.id, photo.id);
                            await openDetail(selected.id);
                            alert('Arrival photo deleted.', 'success');
                        } catch (error) {
                            alert(error.message);
                        } finally {
                            setDeletingPhotoId(null);
                        }
                    },
                },
            ],
        });
    };

    const uploadPackingList = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (files.length !== 1) {
            alert('Choose one PDF packing list.', 'warning');
            return;
        }
        const [file] = files;
        if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf') || file.size > 20 * 1024 * 1024) {
            alert('Packing list must be a PDF no larger than 20MB.', 'warning');
            return;
        }
        setPackingListBusy(true);
        try {
            await uploadInboundBookingPackingList(selected.id, file);
            await openDetail(selected.id);
            alert(selected.packing_list ? 'Packing list replaced.' : 'Packing list uploaded.', 'success');
        } catch (error) {
            alert(error.message);
        } finally {
            setPackingListBusy(false);
        }
    };

    const handlePackingListInput = async (event) => {
        await uploadPackingList(event.target.files);
        event.target.value = '';
    };

    const handlePackingListDrop = (event) => {
        event.preventDefault();
        packingListDragDepthRef.current = 0;
        setPackingListDragActive(false);
        if (!packingListBusy) uploadPackingList(event.dataTransfer.files);
    };

    const handlePackingListDragEnter = (event) => {
        event.preventDefault();
        packingListDragDepthRef.current += 1;
        setPackingListDragActive(true);
    };

    const handlePackingListDragLeave = (event) => {
        event.preventDefault();
        packingListDragDepthRef.current = Math.max(0, packingListDragDepthRef.current - 1);
        if (packingListDragDepthRef.current === 0) setPackingListDragActive(false);
    };

    const downloadPackingList = async () => {
        setPackingListBusy(true);
        try {
            const { blob, filename } = await downloadInboundBookingPackingList(selected.id);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            alert(error.message);
        } finally {
            setPackingListBusy(false);
        }
    };

    const confirmPackingListDeletion = () => {
        showAlert({
            title: 'Delete packing list',
            message: `Delete ${selected.packing_list.filename}? This cannot be undone.`,
            type: 'danger',
            buttons: [
                { label: 'Keep packing list', type: 'secondary' },
                {
                    label: 'Delete packing list',
                    type: 'danger',
                    onClick: async () => {
                        setPackingListBusy(true);
                        try {
                            await deleteInboundBookingPackingList(selected.id);
                            await openDetail(selected.id);
                            alert('Packing list deleted.', 'success');
                        } catch (error) {
                            alert(error.message);
                        } finally {
                            setPackingListBusy(false);
                        }
                    },
                },
            ],
        });
    };

    const labelCount = selected?.items?.every((item) => Number(item.cases_per_pallet) > 0)
        ? selected.items.reduce((total, item) => (
            total + Math.ceil(Number(item.expected_quantity) / Number(item.cases_per_pallet)) + 1
        ), 0)
        : null;

    const downloadLabels = async () => {
        setDownloadingLabels(true);
        try {
            const { blob, filename } = await downloadInboundBookingLabels(selected.id);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            alert(error.message);
        } finally {
            setDownloadingLabels(false);
        }
    };

    if (showForm) {
        return (
            <form className="inbound-booking-form" onSubmit={saveBooking}>
                <div className="inbound-heading-row">
                    <div><h3>{editingId ? 'Amend inbound booking' : 'Request inbound booking'}</h3><p>Expected goods only. Warehouse receipt remains a separate operation.</p></div>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Back</button>
                </div>
                {!isCustomer && (
                    <div className="inbound-booking-customer" aria-label="Selected customer">
                        <span>Booking for</span>
                        <strong>{selectedCustomerName || selected?.customer_company || `Customer ${customerId}`}</strong>
                    </div>
                )}
                <div className="inbound-form-grid">
                    <label>Supplier<input required value={form.supplier} onChange={(e) => setField('supplier', e.target.value)} /></label>
                    <label>Load type<select value={form.vehicle_type} onChange={(e) => setField('vehicle_type', e.target.value)}><option value="container">Container</option><option value="trailer">Trailer</option></select></label>
                    {form.vehicle_type === 'container' && <label>Container size<select value={form.container_size} onChange={(e) => setField('container_size', e.target.value)}><option value="20ft">20ft</option><option value="40ft">40ft</option></select></label>}
                    <label>{form.vehicle_type === 'container' ? 'Container number' : 'Registration'}<input required={form.vehicle_type === 'container'} value={form.vehicle_reference} onChange={(e) => setField('vehicle_reference', e.target.value)} /></label>
                    <label>Delivery reference<input value={form.delivery_reference} onChange={(e) => setField('delivery_reference', e.target.value)} /></label>
                    <label>Delivery date<input type="date" required value={form.requested_date} onChange={(e) => setField('requested_date', e.target.value)} /></label>
                    <label>Delivery time<input type="time" required value={form.requested_time} onChange={(e) => setField('requested_time', e.target.value)} /></label>
                    <label>Unloading<select value={form.unloading_method} onChange={(e) => setField('unloading_method', e.target.value)}><option value="palletised">Palletised</option><option value="handball">Handball</option></select></label>
                </div>
                <label className="inbound-notes">Customer notes<textarea rows="3" value={form.customer_notes} onChange={(e) => setField('customer_notes', e.target.value)} /></label>
                <div className="inbound-lines-heading"><h4>Expected products</h4><button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setForm((current) => ({ ...current, items: [...current.items, { ...emptyForm.items[0] }] }))}>Add line</button></div>
                <div className="inbound-lines">
                    {form.items.map((item, index) => (
                        <div className="inbound-line" key={index}>
                            <label>Product<ProductCombobox products={products} value={item.product_id} onChange={(productId) => setItem(index, 'product_id', productId)} /></label>
                            <label>Expected cases<input type="number" min="1" required value={item.expected_quantity} onChange={(e) => setItem(index, 'expected_quantity', e.target.value)} /></label>
                            <label>Cases per pallet<input type="number" min="1" value={item.cases_per_pallet} onChange={(e) => setItem(index, 'cases_per_pallet', e.target.value)} /></label>
                            <label>Description<input value={item.goods_description} onChange={(e) => setItem(index, 'goods_description', e.target.value)} /></label>
                            {form.items.length > 1 && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setForm((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))}>Remove</button>}
                        </div>
                    ))}
                </div>
                <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : editingId ? 'Submit amendment' : 'Request booking'}</button>
            </form>
        );
    }

    return (
        <div className="inbound-bookings">
            <div className="inbound-heading-row">
                <div><h3>Inbound bookings</h3><p>Plan expected deliveries, approvals and onsite unloading.</p></div>
                <button className="btn btn-primary" onClick={startCreate}>New booking</button>
            </div>
            <div className="inbound-workspace">
                <div className="inbound-booking-list">
                    {bookings.length === 0 && <p className="inbound-empty">No inbound bookings found.</p>}
                    {bookings.map((booking) => (
                        <button key={booking.id} className={`inbound-booking-row ${selected?.id === booking.id ? 'selected' : ''}`} onClick={() => openDetail(booking.id)}>
                            <span><strong>{booking.supplier}</strong><small>{booking.customer_company || 'Customer unavailable'} · {booking.requested_date} · {booking.requested_time?.slice(0, 5)}</small></span>
                            <span><span className={`inbound-status status-${booking.status}`}>{STATUS_LABELS[booking.status]}</span><small>{booking.vehicle_reference}</small></span>
                        </button>
                    ))}
                </div>
                <div className="inbound-detail">
                    {!selected ? <div className="inbound-empty">Select a booking to view its timeline and goods.</div> : <>
                        <div className="inbound-heading-row"><div><h4>{selected.supplier}</h4><p>{selected.vehicle_type === 'container' ? `${selected.container_size} container · ${selected.vehicle_reference}` : ['Trailer', selected.vehicle_reference].filter(Boolean).join(' · ')}</p></div><span className={`inbound-status status-${selected.status}`}>{STATUS_LABELS[selected.status]}</span></div>
                        <dl className="inbound-facts"><div><dt>Requested slot</dt><dd>{selected.requested_date} at {selected.requested_time?.slice(0, 5)}</dd></div><div><dt>Unloading</dt><dd>{selected.unloading_method}</dd></div><div><dt>Delivery ref</dt><dd>{selected.delivery_reference || '—'}</dd></div><div><dt>Customer</dt><dd>{selected.customer_company || 'Customer unavailable'}</dd></div></dl>
                        <div className="inbound-time-window">
                            <div><span>Start time</span><strong>{formatDateTime(selected.arrived_onsite_at)}</strong></div>
                            <div><span>End time</span><strong>{formatDateTime(selected.completed_at)}</strong></div>
                        </div>
                        <div className="inbound-actions">
                            {isStaff && (NEXT_ACTIONS[selected.status] || []).map(([status, label]) => <button key={status} className="btn btn-primary btn-sm" disabled={busy} onClick={() => transition(status)}>{label}</button>)}
                            {['requested', 'accepted', 'rejected'].includes(selected.status) && <button className="btn btn-outline-primary btn-sm" onClick={startEdit}>Amend</button>}
                            {['requested', 'accepted'].includes(selected.status) && <button className="btn btn-outline-danger btn-sm" onClick={cancel}>Cancel</button>}
                        </div>
                        {selected.rejection_reason && <p className="inbound-rejection"><strong>Rejected:</strong> {selected.rejection_reason}</p>}
                        <section>
                            <div className="inbound-section-heading">
                                <h5>Expected products</h5>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        disabled={!labelCount || downloadingLabels}
                                        title={labelCount ? `Download ${labelCount} inbound stock labels` : 'Add cases per pallet to every product line first'}
                                        onClick={downloadLabels}
                                    >
                                        <FontAwesomeIcon icon={downloadingLabels ? faSpinner : faDownload} spin={downloadingLabels} style={{ width: 14 }} />
                                        {downloadingLabels ? 'Generating…' : labelCount ? `Download ${labelCount} labels` : 'Labels unavailable'}
                                    </button>
                                )}
                            </div>
                            <table><thead><tr><th>Product</th><th>Cases</th><th>Cases / pallet</th><th>Pallets</th></tr></thead><tbody>{selected.items.map((item) => <tr key={item.id}><td>{products.find((product) => product.id === item.product_id)?.product_name || item.goods_description || item.product_id}</td><td>{item.expected_quantity}</td><td>{item.cases_per_pallet || '—'}</td><td>{item.cases_per_pallet ? Math.ceil(Number(item.expected_quantity) / Number(item.cases_per_pallet)) : '—'}</td></tr>)}</tbody></table>
                        </section>
                        <section><h5>Status history</h5><ol className="inbound-timeline">{selected.history.map((entry) => <li key={entry.id}><strong>{STATUS_LABELS[entry.to_status]}</strong><span>{formatDateTime(entry.created_at)}</span>{entry.note && <small>{entry.note}</small>}</li>)}</ol></section>
                        <section><h5>Comments</h5>{selected.comments.map((entry) => <div className="inbound-comment" key={entry.id}><p>{entry.comment}</p><small>{formatDateTime(entry.created_at)}</small></div>)}<form className="inbound-comment-form" onSubmit={submitComment}><input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" /><button className="btn btn-outline-primary btn-sm">Add</button></form></section>
                        <section className="inbound-packing-list-manager">
                            <div className="inbound-section-heading">
                                <div><h5>Packing list</h5><span>PDF · up to 20 MB</span></div>
                                {isAdmin && selected.packing_list && (
                                    <button type="button" className="btn btn-outline-primary btn-sm" disabled={packingListBusy} onClick={downloadPackingList}>
                                        <FontAwesomeIcon icon={packingListBusy ? faSpinner : faDownload} spin={packingListBusy} style={{ width: 14 }} />
                                        Download PDF
                                    </button>
                                )}
                            </div>
                            {(isAdmin || isCustomer) && selected.status !== 'completed' && (
                                <div
                                    className={`inbound-packing-list-dropzone ${packingListDragActive ? 'active' : ''} ${packingListBusy ? 'disabled' : ''}`}
                                    onDragEnter={handlePackingListDragEnter}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDragLeave={handlePackingListDragLeave}
                                    onDrop={handlePackingListDrop}
                                >
                                    <div className="inbound-packing-list-drop-copy">
                                        <strong>{packingListBusy ? 'Processing packing list' : packingListDragActive ? 'Release to upload' : selected.packing_list ? 'Drag a replacement PDF here' : 'Drag packing list here'}</strong>
                                        <span>{selected.packing_list ? 'Uploading replaces the current packing list.' : 'One PDF can be attached to this booking.'}</span>
                                    </div>
                                    <button type="button" className="inbound-photo-browse" disabled={packingListBusy} onClick={() => packingListInputRef.current?.click()}>
                                        <FontAwesomeIcon icon={packingListBusy ? faSpinner : faUpload} spin={packingListBusy} style={{ width: 14 }} />
                                        <span>{selected.packing_list ? 'Replace PDF' : 'Choose PDF'}</span>
                                    </button>
                                    <input ref={packingListInputRef} hidden disabled={packingListBusy} type="file" accept="application/pdf,.pdf" onChange={handlePackingListInput} />
                                </div>
                            )}
                            {selected.packing_list ? (
                                <div className="inbound-packing-list-file">
                                    <FontAwesomeIcon icon={faFilePdf} style={{ width: 24 }} />
                                    <div><strong title={selected.packing_list.filename}>{selected.packing_list.filename}</strong><span>{formatFileSize(selected.packing_list.file_size)} · uploaded {formatDateTime(selected.packing_list.updated_at || selected.packing_list.created_at)}</span></div>
                                    {(isAdmin || isCustomer) && selected.status !== 'completed' && (
                                        <button type="button" title="Delete packing list" aria-label={`Delete ${selected.packing_list.filename}`} disabled={packingListBusy} onClick={confirmPackingListDeletion}>
                                            <FontAwesomeIcon icon={packingListBusy ? faSpinner : faTrashCan} spin={packingListBusy} style={{ width: 14 }} />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="inbound-packing-list-empty"><FontAwesomeIcon icon={faFilePdf} style={{ width: 24 }} /><span>No packing list attached</span></div>
                            )}
                            {selected.status === 'completed' && <small className="inbound-packing-list-locked">This booking is complete. Its packing list can no longer be changed.</small>}
                        </section>
                        <section className="inbound-photo-manager">
                            <div className="inbound-photo-heading">
                                <div><h5>Arrival photos</h5><span>{selected.photos.length} image{selected.photos.length === 1 ? '' : 's'}</span></div>
                            </div>
                            {isAdmin && (
                                <div
                                    className={`inbound-photo-dropzone ${photoDragActive ? 'active' : ''} ${photoUploadProgress ? 'disabled' : ''}`}
                                    onDragEnter={handlePhotoDragEnter}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDragLeave={handlePhotoDragLeave}
                                    onDrop={handlePhotoDrop}
                                >
                                    <div className="inbound-photo-drop-copy">
                                        <strong>{photoUploadProgress ? `Uploading ${photoUploadProgress.current} of ${photoUploadProgress.total}` : photoDragActive ? 'Release to upload' : 'Drag images here'}</strong>
                                        <span>JPEG, PNG or WebP · up to 10 MB each</span>
                                    </div>
                                    <button type="button" className="inbound-photo-browse" disabled={Boolean(photoUploadProgress)} onClick={() => photoInputRef.current?.click()}>
                                        <FontAwesomeIcon icon={photoUploadProgress ? faSpinner : faUpload} spin={Boolean(photoUploadProgress)} style={{ width: 14 }} />
                                        <span>{photoUploadProgress ? 'Uploading' : 'Choose images'}</span>
                                    </button>
                                    <input ref={photoInputRef} hidden multiple disabled={Boolean(photoUploadProgress)} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoInput} />
                                </div>
                            )}
                            {selected.photos.length === 0 ? (
                                <div className="inbound-photo-empty"><FontAwesomeIcon icon={faImages} style={{ width: 24 }} /><strong>No arrival photos</strong><span>{isAdmin ? 'Upload images after the load arrives onsite.' : 'Photos added by the warehouse will appear here.'}</span></div>
                            ) : (
                                <div className="inbound-photos">
                                    {selected.photos.map((photo) => (
                                        <article className="inbound-photo" key={photo.id}>
                                            <a className="inbound-photo-preview" href={photo.url} target="_blank" rel="noreferrer" aria-label={`Open ${photo.filename}`}>
                                                <img src={photo.url} alt={photo.filename} />
                                                <span><FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ width: 14 }} /> Open</span>
                                            </a>
                                            <div className="inbound-photo-meta"><strong title={photo.filename}>{photo.filename}</strong><small>{formatFileSize(photo.file_size)}</small></div>
                                            {isAdmin && (
                                                <button className="inbound-photo-delete" type="button" title="Delete photo" aria-label={`Delete ${photo.filename}`} disabled={deletingPhotoId === photo.id} onClick={() => confirmPhotoDeletion(photo)}>
                                                    <FontAwesomeIcon icon={deletingPhotoId === photo.id ? faSpinner : faTrashCan} spin={deletingPhotoId === photo.id} style={{ width: 14 }} />
                                                </button>
                                            )}
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    </>}
                </div>
            </div>
        </div>
    );
}
